/**
 * Sidebar navigation with plugin route injection
 */

import { clsx } from 'clsx';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Home, Search, MessageSquare, FileText, Database, Layers, Zap, Package, Users, Shield } from 'lucide-react';
import { NavItem } from '../../types/plugins';
import { pluginRegistry } from '../../plugins/PluginRegistry';
import { usePermissions } from '../../hooks/usePermissions';

interface NavItemButtonProps {
  item: NavItem;
  Icon: any;
  isActive: boolean;
  collapsed: boolean;
  showTooltip: boolean;
  isPlugin?: boolean;
  onNavigate?: (path: string) => void;
  onToggle?: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function NavItemButton({ 
  item, 
  Icon, 
  isActive, 
  collapsed, 
  showTooltip,
  isPlugin = false,
  onNavigate, 
  onToggle,
  onMouseEnter,
  onMouseLeave
}: NavItemButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8
      });
    }
  }, [showTooltip]);
  
  return (
    <div
      className="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        ref={buttonRef}
        onClick={() => {
          onNavigate?.(item.path);
          if (collapsed && onToggle) {
            onToggle();
          }
        }}
        className={clsx(
          'group w-full flex items-center text-sm font-medium transition-all duration-200 text-left border-l-2',
          collapsed ? 'justify-center px-2 py-3 rounded-md' : 'gap-3 px-4 py-3 rounded-lg',
          isActive
            ? isPlugin
              ? 'bg-slate-800/50 text-white border-l-4 border-teal-400 shadow-md'
              : 'bg-slate-800/50 text-white border-l-4 border-blue-400 shadow-md'
            : 'text-slate-300 border-l-2 border-transparent hover:bg-slate-800/30 hover:text-white hover:border-l-4 hover:border-slate-400'
        )}
      >
        {Icon && <Icon className={clsx(
          'transition-all flex-shrink-0',
          collapsed ? 'h-6 w-6' : 'h-5 w-5',
          isActive 
            ? 'text-white' 
            : isPlugin 
              ? 'text-slate-400 dark:text-slate-300 group-hover:text-teal-300' 
              : 'text-slate-400 dark:text-slate-300 group-hover:text-blue-300'
        )} />}
        {!collapsed && (
          <>
            <span>{item.label}</span>
            {item.badge && (
              <span className={clsx(
                "ml-auto text-white px-2 py-0.5 rounded text-xs font-semibold",
                isPlugin ? "bg-teal-600" : "bg-blue-600"
              )}>
                {item.badge}
              </span>
            )}
            {isActive && (
              <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />
            )}
          </>
        )}
      </button>
      
      {/* Tooltip for collapsed state - rendered with fixed positioning */}
      {showTooltip && (
        <div 
          className="fixed z-[9999] px-3 py-2 bg-slate-800 text-white text-sm font-medium rounded-md shadow-lg whitespace-nowrap pointer-events-none border border-slate-600"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {item.label}
          {item.badge && (
            <span className={clsx(
              "ml-2 text-white px-2 py-0.5 rounded text-xs font-semibold",
              isPlugin ? "bg-teal-600" : "bg-blue-600"
            )}>
              {item.badge}
            </span>
          )}
          {/* Arrow pointing to the button */}
          <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-slate-800"></div>
        </div>
      )}
    </div>
  );
}

export interface SidebarProps {
  pluginItems?: NavItem[];
  onNavigate?: (path: string) => void;
  currentPath?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  // Branding props
  logo?: string;
  logoAlt?: string;
  platformTitle?: string;
  platformSubtitle?: string;
}

export function Sidebar({ 
  pluginItems = [], 
  onNavigate, 
  currentPath = '', 
  collapsed = false, 
  onToggle,
  logo = '/logo.svg',
  logoAlt = 'Logo',
  platformTitle = 'TraceKit',
  platformSubtitle = 'Evidence Platform'
}: SidebarProps) {
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  
  // Plugins that replace core navigation items (scalable for future plugins)
  const PLUGINS_THAT_REPLACE_REPORTS = ['rfp-agent'];
  const PLUGINS_THAT_REPLACE_DATA_MANAGEMENT = ['rfp-agent'];
  
  // Check if any plugin that replaces reports is active
  const hasReportsReplacement = PLUGINS_THAT_REPLACE_REPORTS.some(pluginId => 
    pluginRegistry.getPlugin(pluginId) !== undefined
  );
  
  // Check if any plugin that replaces data management is active
  const hasDataManagementReplacement = PLUGINS_THAT_REPLACE_DATA_MANAGEMENT.some(pluginId => 
    pluginRegistry.getPlugin(pluginId) !== undefined
  );

  // Permission mapping for navigation items
  const getItemPermission = (path: string): string | null => {
    if (path === '/data') return 'data_management.read';
    if (path === '/reports') return 'reports.read';
    // Search and Home are always accessible (core features)
    if (path === '/search') return null;
    // Home is always accessible
    return null;
  };

  // Filter items based on permissions
  const coreItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { label: 'Home', icon: Home, path: '/' },
      { label: 'AI Search', icon: Search, path: '/search' },
      // Conditionally include Data Pipeline only if no plugin replaces it
      ...(hasDataManagementReplacement ? [] : [{ label: 'Data Pipeline', icon: Database, path: '/data', description: 'Sources, Knowledge, Jobs, Entities, Chat' }]),
      // Conditionally include Reports only if no plugin replaces it
      ...(hasReportsReplacement ? [] : [{ label: 'Reports', icon: FileText, path: '/reports' }]),
    ];

    // Filter based on permissions (skip if permissions are still loading)
    if (permissionsLoading) {
      return items; // Show all items while loading
    }

    return items.filter(item => {
      const requiredPermission = getItemPermission(item.path);
      if (!requiredPermission) return true; // No permission required
      return hasPermission(requiredPermission);
    });
  }, [hasPermission, permissionsLoading, hasDataManagementReplacement, hasReportsReplacement]);

  // Filter plugin items based on permissions
  const filteredPluginItems = useMemo(() => {
    if (permissionsLoading) {
      return pluginItems; // Show all items while loading
    }

    return pluginItems.filter(item => {
      // Check if item has a requiredPermission property
      if (item.requiredPermission) {
        if (Array.isArray(item.requiredPermission)) {
          return item.requiredPermission.some(perm => hasPermission(perm));
        }
        return hasPermission(item.requiredPermission);
      }
      // If no permission specified, allow access
      return true;
    });
  }, [pluginItems, hasPermission, permissionsLoading]);

  const allItems = [...coreItems, ...filteredPluginItems];
  
  // Single state to track which tooltip is showing (keyed by path)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <div 
      className={clsx(
        "text-white h-screen flex flex-col shadow-2xl transition-all duration-300 ease-in-out relative",
        collapsed ? "w-20" : "w-64"
      )}
      style={{
        background: 'var(--color-sidebar-bg, linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%))'
      }}
    >
      {/* Header with gradient accent */}
      <div 
        className={clsx(
          "border-b transition-all duration-300",
          collapsed ? "p-4" : "p-6"
        )}
        style={{
          borderColor: 'var(--color-sidebar-border, rgba(255, 255, 255, 0.1))',
          background: 'var(--color-sidebar-accent, rgba(59, 130, 246, 0.2))'
        }}
      >
        <div className={clsx(
          "flex items-center transition-all duration-300",
          collapsed ? "justify-center" : "gap-3"
        )}>
          <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg shadow-lg">
            {logo ? (
              <img src={logo} alt={logoAlt} className="h-6 w-6 object-contain" />
            ) : (
              <Shield className="h-6 w-6 text-white" />
            )}
          </div>
          {!collapsed && (
            <div className="transition-opacity duration-300">
              <h1 
                className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent"
                style={{ color: 'var(--color-sidebar-text, #ffffff)' }}
              >
                {platformTitle}
              </h1>
              <p 
                className="text-xs font-medium"
                style={{ color: 'var(--color-sidebar-text-secondary, #cbd5e1)' }}
              >
                {platformSubtitle}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Navigation with modern styling */}
      <nav className={clsx(
        "flex-1 space-y-2 overflow-y-auto overflow-x-visible transition-all duration-300",
        collapsed ? "px-2 py-6" : "px-4 py-6"
      )}>
        {/* Core Items */}
        {coreItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPath === item.path;
          const showTooltip = collapsed && hoveredItem === item.path;
          
          return (
            <NavItemButton
              key={item.path}
              item={item}
              Icon={Icon}
              isActive={isActive}
              collapsed={collapsed}
              showTooltip={showTooltip}
              isPlugin={false}
              onNavigate={onNavigate}
              onToggle={onToggle}
              onMouseEnter={() => collapsed && setHoveredItem(item.path)}
              onMouseLeave={() => setHoveredItem(null)}
            />
          );
        })}

        {/* Plugin Items */}
        {filteredPluginItems.length > 0 && (
          <>
            <div className={clsx(
              "my-4 transition-all duration-300",
              collapsed ? "px-2" : "px-4"
            )}>
              <div className="h-px bg-white/10" />
            </div>
            {filteredPluginItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path || currentPath.startsWith(item.path);
              const showTooltip = collapsed && hoveredItem === item.path;
              
              return (
                <NavItemButton
                  key={item.path}
                  item={item}
                  Icon={Icon}
                  isActive={isActive}
                  collapsed={collapsed}
                  showTooltip={showTooltip}
                  isPlugin={true}
                  onNavigate={onNavigate}
                  onToggle={onToggle}
                  onMouseEnter={() => collapsed && setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                />
              );
            })}
          </>
        )}
      </nav>

      {/* Footer with branding */}
      {!collapsed && (
        <div 
          className="p-6 border-t transition-opacity duration-300"
          style={{
            borderColor: 'var(--color-sidebar-border, rgba(255, 255, 255, 0.1))',
            background: 'linear-gradient(90deg, rgba(15, 23, 42, 0.5), rgba(30, 41, 59, 0.5))'
          }}
        >
          <div 
            className="text-xs text-center font-medium"
            style={{ color: 'var(--color-sidebar-text-secondary, #cbd5e1)' }}
          >
            Powered by {platformTitle?.includes('Keystone') ? 'Sparc AI' : 'TraceOS AI'}
          </div>
          <div 
            className="text-xs text-center mt-1"
            style={{ color: 'var(--color-sidebar-text-secondary, #94a3b8)' }}
          >
            v1.0.0
          </div>
        </div>
      )}
    </div>
  );
}

